import React, { useEffect, useState, useCallback } from 'react';
import { API_BASE_URL } from '../../config';
import './AdminYouTube.css';

// =============================================================================
// Interfaces
// =============================================================================

interface YouTubeChannel {
  id: number;
  channel_id: string;
  channel_name: string;
  rss_url: string;
  is_active: boolean;
  mode: string;
  title_patterns: string[];
  exclude_patterns: string[];
  playlist_ids: string[];
  min_duration_seconds: number;
  max_duration_seconds: number;
  max_videos_per_scan: number;
  scan_lookback_hours: number;
  language: string;
  auto_publish: boolean;
  last_scan_at: string | null;
  last_video_at: string | null;
  total_videos_processed: number;
}

interface YouTubeVideo {
  id: number;
  video_id: string;
  title: string;
  url: string;
  channel_name: string;
  published_at: string | null;
  status: string;
  current_stage: string;
  skip_reason_code: string | null;
  skip_reason: string | null;
  transcript_quality_score: number | null;
  gcs_transcript_path: string | null;
  gcs_article_path: string | null;
  article_id: number | null;
  attempts: number;
  rehydrate_attempts: number;
  last_error: string | null;
  created_at: string | null;
  updated_at: string | null;
}

interface VideoStats {
  by_status: Record<string, number>;
  by_skip_reason: Record<string, number>;
  last_24h: number;
  stuck: number;
  total: number;
}

interface PipelineConfig {
  canary_mode: boolean;
  ai_coach_user_id: string | null;
  gcs_bucket: string | null;
  tasks_queue: string | null;
}

interface Toast {
  id: number;
  message: string;
  type: 'success' | 'error' | 'info';
}

// =============================================================================
// Component
// =============================================================================

const AdminYouTube: React.FC = () => {
  // State
  const [channels, setChannels] = useState<YouTubeChannel[]>([]);
  const [videos, setVideos] = useState<YouTubeVideo[]>([]);
  const [stats, setStats] = useState<VideoStats | null>(null);
  const [config, setConfig] = useState<PipelineConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  // Filters
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [videosLimit, setVideosLimit] = useState<number>(50);
  const [videosOffset, setVideosOffset] = useState<number>(0);
  const [videosTotal, setVideosTotal] = useState<number>(0);

  // Modals
  const [showProcessModal, setShowProcessModal] = useState(false);
  const [processVideoInput, setProcessVideoInput] = useState('');
  const [processChannelName, setProcessChannelName] = useState('GCN');
  const [processForce, setProcessForce] = useState(false);

  const [showSkipModal, setShowSkipModal] = useState(false);
  const [skipVideoId, setSkipVideoId] = useState('');
  const [skipReasonCode, setSkipReasonCode] = useState('NOT_CYCLING');
  const [skipNote, setSkipNote] = useState('');

  // Add Channel Modal
  const [showAddChannelModal, setShowAddChannelModal] = useState(false);
  const [newChannelId, setNewChannelId] = useState('');
  const [newChannelName, setNewChannelName] = useState('');
  const [newChannelMode, setNewChannelMode] = useState('title_match');
  const [newChannelPatterns, setNewChannelPatterns] = useState('');
  const [newChannelExclude, setNewChannelExclude] = useState('');

  // Test Transcript Modal
  const [showTestTranscriptModal, setShowTestTranscriptModal] = useState(false);
  const [testVideoId, setTestVideoId] = useState('');
  const [testResult, setTestResult] = useState<any>(null);

  // Toasts
  const [toasts, setToasts] = useState<Toast[]>([]);

  // Editing channel
  const [editingChannel, setEditingChannel] = useState<number | null>(null);
  const [editedChannelData, setEditedChannelData] = useState<Partial<YouTubeChannel>>({});

  // =============================================================================
  // Toast helpers
  // =============================================================================

  const showToast = useCallback((message: string, type: 'success' | 'error' | 'info') => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, 5000);
  }, []);

  // =============================================================================
  // Data fetching
  // =============================================================================

  const fetchChannels = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/youtube/channels`, {
        credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        setChannels(data.channels);
      }
    } catch (err) {
      console.error('Failed to fetch channels:', err);
    }
  }, []);

  const fetchVideos = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);
      if (searchQuery) params.append('q', searchQuery);
      params.append('limit', videosLimit.toString());
      params.append('offset', videosOffset.toString());

      const res = await fetch(`${API_BASE_URL}/api/admin/youtube/videos?${params}`, {
        credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        setVideos(data.videos);
        setVideosTotal(data.total);
      }
    } catch (err) {
      console.error('Failed to fetch videos:', err);
    }
  }, [statusFilter, searchQuery, videosLimit, videosOffset]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/youtube/videos/stats`, {
        credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        setStats(data);
      }
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  }, []);

  const fetchConfig = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/youtube/config`, {
        credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        setConfig(data);
      }
    } catch (err) {
      console.error('Failed to fetch config:', err);
    }
  }, []);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchChannels(), fetchVideos(), fetchStats(), fetchConfig()]);
      setLoading(false);
    };
    loadData();
  }, [fetchChannels, fetchVideos, fetchStats, fetchConfig]);

  useEffect(() => {
    fetchVideos();
  }, [statusFilter, searchQuery, videosOffset, fetchVideos]);

  // =============================================================================
  // Actions
  // =============================================================================

  const handlePollNow = async () => {
    setActionLoading('poll');
    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/youtube/actions/poll-now`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      });
      const data = await res.json();
      if (res.ok) {
        showToast(`Poll completed: ${data.result?.videos_found || 0} found, ${data.result?.videos_enqueued || 0} enqueued`, 'success');
        fetchVideos();
        fetchStats();
      } else {
        showToast(`Poll failed: ${data.error}`, 'error');
      }
    } catch (err) {
      showToast('Poll failed: Network error', 'error');
    } finally {
      setActionLoading(null);
    }
  };

  const handleRehydrateNow = async () => {
    setActionLoading('rehydrate');
    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/youtube/actions/rehydrate-now`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      });
      const data = await res.json();
      if (res.ok) {
        showToast(`Rehydrate completed: ${data.result?.stuck_found || 0} stuck, ${data.result?.rehydrated || 0} requeued`, 'success');
        fetchVideos();
        fetchStats();
      } else {
        showToast(`Rehydrate failed: ${data.error}`, 'error');
      }
    } catch (err) {
      showToast('Rehydrate failed: Network error', 'error');
    } finally {
      setActionLoading(null);
    }
  };

  const handleProcessVideo = async () => {
    if (!processVideoInput.trim()) {
      showToast('Please enter a video URL or ID', 'error');
      return;
    }

    setActionLoading('process');
    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/youtube/actions/process-video`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          video_url_or_id: processVideoInput,
          channel_name: processChannelName,
          force: processForce
        })
      });
      const data = await res.json();
      if (res.ok) {
        if (data.task_warning) {
          // Video saved but not queued (Cloud Tasks not configured)
          showToast(`Video saved: ${data.title || data.video_id} (not queued - pending)`, 'info');
        } else if (data.task_enqueued) {
          showToast(`Video queued: ${data.title || data.video_id}`, 'success');
        } else {
          showToast(`Video exists: ${data.video_id} (${data.status})`, 'info');
        }
        setShowProcessModal(false);
        setProcessVideoInput('');
        setProcessForce(false);
        fetchVideos();
        fetchStats();
      } else {
        showToast(`Process failed: ${data.error}`, 'error');
      }
    } catch (err) {
      showToast('Process failed: Network error', 'error');
    } finally {
      setActionLoading(null);
    }
  };

  const handleRetryStage = async (videoId: string, stage: string) => {
    setActionLoading(`retry-${videoId}-${stage}`);
    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/youtube/actions/retry-stage`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ video_id: videoId, stage })
      });
      const data = await res.json();
      if (res.ok) {
        showToast(`Retry ${stage} queued for ${videoId}`, 'success');
        fetchVideos();
      } else {
        showToast(`Retry failed: ${data.error}`, 'error');
      }
    } catch (err) {
      showToast('Retry failed: Network error', 'error');
    } finally {
      setActionLoading(null);
    }
  };

  const handleMarkSkip = async () => {
    if (!skipVideoId) return;

    setActionLoading('skip');
    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/youtube/actions/mark-skip`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          video_id: skipVideoId,
          reason_code: skipReasonCode,
          note: skipNote
        })
      });
      const data = await res.json();
      if (res.ok) {
        showToast(`Video ${skipVideoId} marked as skipped`, 'success');
        setShowSkipModal(false);
        setSkipVideoId('');
        setSkipNote('');
        fetchVideos();
        fetchStats();
      } else {
        showToast(`Skip failed: ${data.error}`, 'error');
      }
    } catch (err) {
      showToast('Skip failed: Network error', 'error');
    } finally {
      setActionLoading(null);
    }
  };

  const handleUnskip = async (videoId: string) => {
    if (!window.confirm(`Unskip video ${videoId} and restart processing?`)) return;

    setActionLoading(`unskip-${videoId}`);
    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/youtube/actions/unskip`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ video_id: videoId })
      });
      const data = await res.json();
      if (res.ok) {
        showToast(`Video ${videoId} unskipped and requeued`, 'success');
        fetchVideos();
        fetchStats();
      } else {
        showToast(`Unskip failed: ${data.error}`, 'error');
      }
    } catch (err) {
      showToast('Unskip failed: Network error', 'error');
    } finally {
      setActionLoading(null);
    }
  };

  // =============================================================================
  // Channel editing
  // =============================================================================

  const handleEditChannel = (channel: YouTubeChannel) => {
    setEditingChannel(channel.id);
    setEditedChannelData({
      is_active: channel.is_active,
      mode: channel.mode,
      title_patterns: channel.title_patterns,
      exclude_patterns: channel.exclude_patterns,
      max_videos_per_scan: channel.max_videos_per_scan,
      scan_lookback_hours: channel.scan_lookback_hours,
      language: channel.language,
      auto_publish: channel.auto_publish,
    });
  };

  const handleSaveChannel = async (channelId: number) => {
    setActionLoading(`save-channel-${channelId}`);
    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/youtube/channels/${channelId}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editedChannelData)
      });
      const data = await res.json();
      if (res.ok) {
        showToast('Channel updated successfully', 'success');
        setEditingChannel(null);
        fetchChannels();
      } else {
        showToast(`Update failed: ${data.error}`, 'error');
      }
    } catch (err) {
      showToast('Update failed: Network error', 'error');
    } finally {
      setActionLoading(null);
    }
  };

  const handleCancelEdit = () => {
    setEditingChannel(null);
    setEditedChannelData({});
  };

  const handleAddChannel = async () => {
    if (!newChannelId.trim() || !newChannelName.trim()) {
      showToast('Channel ID and Name are required', 'error');
      return;
    }

    setActionLoading('add-channel');
    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/youtube/channels`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          channel_id: newChannelId.trim(),
          channel_name: newChannelName.trim(),
          mode: newChannelMode,
          title_patterns: newChannelPatterns.split('\n').filter(p => p.trim()),
          exclude_patterns: newChannelExclude.split('\n').filter(p => p.trim()),
        })
      });
      const data = await res.json();
      if (res.ok) {
        showToast(`Channel "${newChannelName}" added successfully`, 'success');
        setShowAddChannelModal(false);
        setNewChannelId('');
        setNewChannelName('');
        setNewChannelPatterns('');
        setNewChannelExclude('');
        fetchChannels();
      } else {
        showToast(`Add failed: ${data.error}`, 'error');
      }
    } catch (err) {
      showToast('Add failed: Network error', 'error');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeleteChannel = async (channelId: number, channelName: string) => {
    if (!window.confirm(`Delete channel "${channelName}"? This cannot be undone.`)) return;

    setActionLoading(`delete-channel-${channelId}`);
    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/youtube/channels/${channelId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      const data = await res.json();
      if (res.ok) {
        showToast(`Channel "${channelName}" deleted`, 'success');
        fetchChannels();
      } else {
        showToast(`Delete failed: ${data.error}`, 'error');
      }
    } catch (err) {
      showToast('Delete failed: Network error', 'error');
    } finally {
      setActionLoading(null);
    }
  };

  const handleTestTranscript = async () => {
    if (!testVideoId.trim()) {
      showToast('Please enter a video ID or URL', 'error');
      return;
    }

    setActionLoading('test-transcript');
    setTestResult(null);
    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/youtube/test-transcript`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ video_id: testVideoId })
      });
      const data = await res.json();
      setTestResult(data);
      if (data.success) {
        showToast(`Transcript fetched: ${data.text_length} chars, quality: ${(data.quality_score * 100).toFixed(0)}%`, 'success');
      } else {
        showToast(`Transcript failed: ${data.message || data.error}`, 'error');
      }
    } catch (err) {
      showToast('Test failed: Network error', 'error');
      setTestResult({ success: false, error: 'network', message: 'Network error' });
    } finally {
      setActionLoading(null);
    }
  };

  // =============================================================================
  // Helpers
  // =============================================================================

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('pl-PL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'saved':
      case 'published': return 'badge-success';
      case 'pending': return 'badge-warning';
      case 'transcribing':
      case 'generating':
      case 'saving': return 'badge-info';
      case 'skipped': return 'badge-secondary';
      case 'dead':
      case 'failed': return 'badge-danger';
      default: return 'badge-secondary';
    }
  };

  const totalPages = Math.ceil(videosTotal / videosLimit);
  const currentPage = Math.floor(videosOffset / videosLimit) + 1;

  // =============================================================================
  // Render
  // =============================================================================

  if (loading) {
    return <div className="card">Loading YouTube Pipeline...</div>;
  }

  return (
    <div className="youtube-admin-container">
      {/* Toasts */}
      <div className="toast-container">
        {toasts.map(toast => (
          <div key={toast.id} className={`toast toast-${toast.type}`}>
            {toast.message}
          </div>
        ))}
      </div>

      {/* Header */}
      <div className="card youtube-header">
        <div className="header-content">
          <h2>YouTube &rarr; Blog Generator</h2>
          {config?.canary_mode && (
            <span className="canary-badge">CANARY MODE</span>
          )}
        </div>
        <div className="header-actions">
          <button
            className="btn-action btn-primary"
            onClick={handlePollNow}
            disabled={actionLoading === 'poll'}
          >
            {actionLoading === 'poll' ? 'Polling...' : 'Poll Now'}
          </button>
          <button
            className="btn-action btn-secondary"
            onClick={handleRehydrateNow}
            disabled={actionLoading === 'rehydrate'}
          >
            {actionLoading === 'rehydrate' ? 'Rehydrating...' : 'Rehydrate Now'}
          </button>
          <button
            className="btn-action btn-outline"
            onClick={() => setShowProcessModal(true)}
          >
            + Add Video
          </button>
          <button
            className="btn-action btn-outline"
            onClick={() => { setShowTestTranscriptModal(true); setTestResult(null); setTestVideoId(''); }}
          >
            Test Transcript
          </button>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="youtube-stats">
          <div className="stat-card">
            <span className="stat-value">{stats.total}</span>
            <span className="stat-label">Total</span>
          </div>
          <div className="stat-card">
            <span className="stat-value">{stats.last_24h}</span>
            <span className="stat-label">Last 24h</span>
          </div>
          <div className="stat-card stat-success">
            <span className="stat-value">{(stats.by_status.saved || 0) + (stats.by_status.published || 0)}</span>
            <span className="stat-label">Completed</span>
          </div>
          <div className="stat-card stat-warning">
            <span className="stat-value">{stats.by_status.pending || 0}</span>
            <span className="stat-label">Pending</span>
          </div>
          <div className="stat-card stat-info">
            <span className="stat-value">
              {(stats.by_status.transcribing || 0) + (stats.by_status.generating || 0) + (stats.by_status.saving || 0)}
            </span>
            <span className="stat-label">Processing</span>
          </div>
          <div className="stat-card stat-secondary">
            <span className="stat-value">{stats.by_status.skipped || 0}</span>
            <span className="stat-label">Skipped</span>
          </div>
          <div className="stat-card stat-danger">
            <span className="stat-value">{stats.stuck || 0}</span>
            <span className="stat-label">Stuck</span>
          </div>
        </div>
      )}

      {/* Channels Section */}
      <div className="card">
        <div className="section-header">
          <h3>Channels</h3>
          <button
            className="btn-action btn-outline"
            onClick={() => setShowAddChannelModal(true)}
          >
            + Add Channel
          </button>
        </div>
        <div className="table-container">
          <table className="youtube-table channels-table">
            <thead>
              <tr>
                <th>Active</th>
                <th>Channel</th>
                <th>Mode</th>
                <th>Title Patterns</th>
                <th>Exclude</th>
                <th>Max/Scan</th>
                <th>Lookback</th>
                <th>Language</th>
                <th>Auto Publish</th>
                <th>Last Scan</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {channels.map(channel => (
                <tr key={channel.id}>
                  <td>
                    {editingChannel === channel.id ? (
                      <input
                        type="checkbox"
                        checked={editedChannelData.is_active ?? channel.is_active}
                        onChange={(e) => setEditedChannelData(prev => ({ ...prev, is_active: e.target.checked }))}
                      />
                    ) : (
                      <span className={`active-indicator ${channel.is_active ? 'active' : 'inactive'}`}>
                        {channel.is_active ? '●' : '○'}
                      </span>
                    )}
                  </td>
                  <td>
                    <strong>{channel.channel_name}</strong>
                    <div className="channel-id">{channel.channel_id}</div>
                  </td>
                  <td>
                    {editingChannel === channel.id ? (
                      <select
                        value={editedChannelData.mode ?? channel.mode}
                        onChange={(e) => setEditedChannelData(prev => ({ ...prev, mode: e.target.value }))}
                      >
                        <option value="all">All</option>
                        <option value="title_match">Title Match</option>
                        <option value="playlist_only">Playlist Only</option>
                        <option value="weekly_only">Weekly Only</option>
                      </select>
                    ) : (
                      <span className="mode-badge">{channel.mode}</span>
                    )}
                  </td>
                  <td className="patterns-cell">
                    {editingChannel === channel.id ? (
                      <textarea
                        value={(editedChannelData.title_patterns ?? channel.title_patterns).join('\n')}
                        onChange={(e) => setEditedChannelData(prev => ({
                          ...prev,
                          title_patterns: e.target.value.split('\n').filter(p => p.trim())
                        }))}
                        rows={3}
                      />
                    ) : (
                      channel.title_patterns.length > 0 ? (
                        <div className="patterns-list">
                          {channel.title_patterns.slice(0, 3).map((p, i) => (
                            <span key={i} className="pattern-chip">{p}</span>
                          ))}
                          {channel.title_patterns.length > 3 && (
                            <span className="pattern-more">+{channel.title_patterns.length - 3}</span>
                          )}
                        </div>
                      ) : '-'
                    )}
                  </td>
                  <td className="patterns-cell">
                    {editingChannel === channel.id ? (
                      <textarea
                        value={(editedChannelData.exclude_patterns ?? channel.exclude_patterns).join('\n')}
                        onChange={(e) => setEditedChannelData(prev => ({
                          ...prev,
                          exclude_patterns: e.target.value.split('\n').filter(p => p.trim())
                        }))}
                        rows={2}
                      />
                    ) : (
                      channel.exclude_patterns.length > 0 ? (
                        <div className="patterns-list">
                          {channel.exclude_patterns.slice(0, 2).map((p, i) => (
                            <span key={i} className="pattern-chip exclude">{p}</span>
                          ))}
                          {channel.exclude_patterns.length > 2 && (
                            <span className="pattern-more">+{channel.exclude_patterns.length - 2}</span>
                          )}
                        </div>
                      ) : '-'
                    )}
                  </td>
                  <td>
                    {editingChannel === channel.id ? (
                      <input
                        type="number"
                        value={editedChannelData.max_videos_per_scan ?? channel.max_videos_per_scan}
                        onChange={(e) => setEditedChannelData(prev => ({ ...prev, max_videos_per_scan: parseInt(e.target.value) || 5 }))}
                        min={1}
                        max={20}
                        style={{ width: '60px' }}
                      />
                    ) : (
                      channel.max_videos_per_scan
                    )}
                  </td>
                  <td>
                    {editingChannel === channel.id ? (
                      <input
                        type="number"
                        value={editedChannelData.scan_lookback_hours ?? channel.scan_lookback_hours}
                        onChange={(e) => setEditedChannelData(prev => ({ ...prev, scan_lookback_hours: parseInt(e.target.value) || 48 }))}
                        min={1}
                        max={168}
                        style={{ width: '60px' }}
                      />
                    ) : (
                      `${channel.scan_lookback_hours}h`
                    )}
                  </td>
                  <td>
                    {editingChannel === channel.id ? (
                      <input
                        type="text"
                        value={editedChannelData.language ?? channel.language}
                        onChange={(e) => setEditedChannelData(prev => ({ ...prev, language: e.target.value }))}
                        style={{ width: '50px' }}
                      />
                    ) : (
                      channel.language
                    )}
                  </td>
                  <td>
                    {editingChannel === channel.id ? (
                      <input
                        type="checkbox"
                        checked={editedChannelData.auto_publish ?? channel.auto_publish}
                        onChange={(e) => setEditedChannelData(prev => ({ ...prev, auto_publish: e.target.checked }))}
                      />
                    ) : (
                      channel.auto_publish ? 'Yes' : 'No'
                    )}
                  </td>
                  <td className="date-cell">
                    {formatDate(channel.last_scan_at)}
                  </td>
                  <td>
                    {editingChannel === channel.id ? (
                      <div className="action-buttons">
                        <button
                          className="btn-small btn-save"
                          onClick={() => handleSaveChannel(channel.id)}
                          disabled={actionLoading === `save-channel-${channel.id}`}
                        >
                          Save
                        </button>
                        <button
                          className="btn-small btn-cancel"
                          onClick={handleCancelEdit}
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <div className="action-buttons">
                        <button
                          className="btn-small btn-edit"
                          onClick={() => handleEditChannel(channel)}
                        >
                          Edit
                        </button>
                        <button
                          className="btn-small btn-delete"
                          onClick={() => handleDeleteChannel(channel.id, channel.channel_name)}
                          disabled={actionLoading === `delete-channel-${channel.id}`}
                        >
                          Del
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Videos Section */}
      <div className="card">
        <div className="section-header">
          <h3>Videos</h3>
          <button
            className="btn-action btn-outline"
            onClick={() => setShowProcessModal(true)}
          >
            + Add Video
          </button>
        </div>

        {/* Filters */}
        <div className="youtube-filters">
          <div className="filter-group">
            <label>Status:</label>
            <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setVideosOffset(0); }}>
              <option value="">All</option>
              <option value="pending">Pending</option>
              <option value="transcribing">Transcribing</option>
              <option value="transcribed">Transcribed</option>
              <option value="generating">Generating</option>
              <option value="generated">Generated</option>
              <option value="saving">Saving</option>
              <option value="saved">Saved</option>
              <option value="published">Published</option>
              <option value="skipped">Skipped</option>
              <option value="dead">Dead</option>
            </select>
          </div>
          <div className="filter-group">
            <label>Search:</label>
            <input
              type="text"
              placeholder="Title or video ID..."
              value={searchQuery}
              onChange={(e) => { setSearchQuery(e.target.value); setVideosOffset(0); }}
            />
          </div>
          <button
            className="btn-refresh"
            onClick={() => { fetchVideos(); fetchStats(); }}
          >
            Refresh
          </button>
        </div>

        {/* Videos Table */}
        <div className="table-container">
          <table className="youtube-table videos-table">
            <thead>
              <tr>
                <th>Status</th>
                <th>Title</th>
                <th>Channel</th>
                <th>Published</th>
                <th>Quality</th>
                <th>Skip Reason</th>
                <th>Article</th>
                <th>Updated</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {videos.map(video => (
                <tr key={video.id}>
                  <td>
                    <span className={`status-badge ${getStatusBadgeClass(video.status)}`}>
                      {video.status}
                    </span>
                    {video.current_stage && video.status !== 'skipped' && video.status !== 'dead' && video.status !== 'saved' && (
                      <div className="stage-info">{video.current_stage}</div>
                    )}
                  </td>
                  <td className="title-cell">
                    <a href={video.url} target="_blank" rel="noopener noreferrer" title={video.title}>
                      {video.title.length > 50 ? video.title.substring(0, 50) + '...' : video.title}
                    </a>
                    <div className="video-id">{video.video_id}</div>
                  </td>
                  <td>{video.channel_name}</td>
                  <td className="date-cell">{formatDate(video.published_at)}</td>
                  <td className="quality-cell">
                    {video.transcript_quality_score != null ? (
                      <span className={`quality-score ${video.transcript_quality_score >= 0.6 ? 'good' : 'bad'}`}>
                        {(video.transcript_quality_score * 100).toFixed(0)}%
                      </span>
                    ) : '-'}
                  </td>
                  <td>
                    {video.skip_reason_code ? (
                      <span className="skip-reason" title={video.skip_reason || ''}>
                        {video.skip_reason_code}
                      </span>
                    ) : '-'}
                  </td>
                  <td>
                    {video.article_id ? (
                      <a href={`?view=blog-admin&article=${video.article_id}`} className="article-link">
                        #{video.article_id}
                      </a>
                    ) : '-'}
                  </td>
                  <td className="date-cell">{formatDate(video.updated_at)}</td>
                  <td>
                    <div className="action-buttons">
                      {video.status === 'skipped' ? (
                        <button
                          className="btn-small btn-unskip"
                          onClick={() => handleUnskip(video.video_id)}
                          disabled={actionLoading === `unskip-${video.video_id}`}
                        >
                          Unskip
                        </button>
                      ) : video.status !== 'saved' && video.status !== 'published' ? (
                        <>
                          <button
                            className="btn-small"
                            onClick={() => handleRetryStage(video.video_id, 'transcript')}
                            disabled={!!actionLoading}
                            title="Retry transcript"
                          >
                            T
                          </button>
                          <button
                            className="btn-small"
                            onClick={() => handleRetryStage(video.video_id, 'generate')}
                            disabled={!!actionLoading || !video.gcs_transcript_path}
                            title="Retry generate"
                          >
                            G
                          </button>
                          <button
                            className="btn-small"
                            onClick={() => handleRetryStage(video.video_id, 'save')}
                            disabled={!!actionLoading || !video.gcs_article_path}
                            title="Retry save"
                          >
                            S
                          </button>
                          <button
                            className="btn-small btn-skip"
                            onClick={() => { setSkipVideoId(video.video_id); setShowSkipModal(true); }}
                            title="Skip"
                          >
                            Skip
                          </button>
                        </>
                      ) : null}
                    </div>
                  </td>
                </tr>
              ))}
              {videos.length === 0 && (
                <tr>
                  <td colSpan={9} className="no-data">No videos found</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="pagination">
            <button
              disabled={currentPage <= 1}
              onClick={() => setVideosOffset((currentPage - 2) * videosLimit)}
            >
              Previous
            </button>
            <span className="page-info">
              Page {currentPage} of {totalPages} ({videosTotal} total)
            </span>
            <button
              disabled={currentPage >= totalPages}
              onClick={() => setVideosOffset(currentPage * videosLimit)}
            >
              Next
            </button>
          </div>
        )}
      </div>

      {/* Process Video Modal */}
      {showProcessModal && (
        <div className="modal-overlay" onClick={() => setShowProcessModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h3>Add YouTube Video</h3>
            <div className="modal-content">
              <div className="form-group">
                <label>YouTube URL or Video ID:</label>
                <input
                  type="text"
                  value={processVideoInput}
                  onChange={(e) => setProcessVideoInput(e.target.value)}
                  placeholder="dQw4w9WgXcQ or https://youtube.com/watch?v=..."
                />
              </div>
              <div className="form-group">
                <label>Channel Name:</label>
                <select value={processChannelName} onChange={(e) => setProcessChannelName(e.target.value)}>
                  {channels.map(c => (
                    <option key={c.id} value={c.channel_name}>{c.channel_name}</option>
                  ))}
                  <option value="Manual">Manual</option>
                </select>
              </div>
              <div className="form-group checkbox">
                <label>
                  <input
                    type="checkbox"
                    checked={processForce}
                    onChange={(e) => setProcessForce(e.target.checked)}
                  />
                  Force reprocess (if already exists)
                </label>
              </div>
            </div>
            <div className="modal-actions">
              <button className="btn-cancel" onClick={() => setShowProcessModal(false)}>Cancel</button>
              <button
                className="btn-primary"
                onClick={handleProcessVideo}
                disabled={actionLoading === 'process'}
              >
                {actionLoading === 'process' ? 'Adding...' : 'Add Video'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Skip Modal */}
      {showSkipModal && (
        <div className="modal-overlay" onClick={() => setShowSkipModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h3>Skip Video</h3>
            <div className="modal-content">
              <p>Video: <strong>{skipVideoId}</strong></p>
              <div className="form-group">
                <label>Reason Code:</label>
                <select value={skipReasonCode} onChange={(e) => setSkipReasonCode(e.target.value)}>
                  <option value="NOT_CYCLING">NOT_CYCLING</option>
                  <option value="TRANSCRIPT_UNAVAILABLE">TRANSCRIPT_UNAVAILABLE</option>
                  <option value="LOW_QUALITY">LOW_QUALITY</option>
                  <option value="TOO_SHORT">TOO_SHORT</option>
                </select>
              </div>
              <div className="form-group">
                <label>Note (optional):</label>
                <input
                  type="text"
                  value={skipNote}
                  onChange={(e) => setSkipNote(e.target.value)}
                  placeholder="Additional note..."
                />
              </div>
            </div>
            <div className="modal-actions">
              <button className="btn-cancel" onClick={() => setShowSkipModal(false)}>Cancel</button>
              <button
                className="btn-danger"
                onClick={handleMarkSkip}
                disabled={actionLoading === 'skip'}
              >
                {actionLoading === 'skip' ? 'Skipping...' : 'Mark as Skipped'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Channel Modal */}
      {showAddChannelModal && (
        <div className="modal-overlay" onClick={() => setShowAddChannelModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h3>Add YouTube Channel</h3>
            <div className="modal-content">
              <div className="form-group">
                <label>Channel ID:</label>
                <input
                  type="text"
                  value={newChannelId}
                  onChange={(e) => setNewChannelId(e.target.value)}
                  placeholder="e.g., UCuTaETsuCOkJ0H_GAztWt0Q"
                />
                <small className="hint">Find it in the channel's YouTube URL</small>
              </div>
              <div className="form-group">
                <label>Channel Name:</label>
                <input
                  type="text"
                  value={newChannelName}
                  onChange={(e) => setNewChannelName(e.target.value)}
                  placeholder="e.g., GCN"
                />
              </div>
              <div className="form-group">
                <label>Mode:</label>
                <select value={newChannelMode} onChange={(e) => setNewChannelMode(e.target.value)}>
                  <option value="all">All Videos</option>
                  <option value="title_match">Title Match (recommended)</option>
                  <option value="playlist_only">Playlist Only</option>
                  <option value="weekly_only">Weekly Only</option>
                </select>
              </div>
              <div className="form-group">
                <label>Title Patterns (one per line):</label>
                <textarea
                  value={newChannelPatterns}
                  onChange={(e) => setNewChannelPatterns(e.target.value)}
                  placeholder="Racing News&#10;Weekly Recap"
                  rows={3}
                />
              </div>
              <div className="form-group">
                <label>Exclude Patterns (one per line):</label>
                <textarea
                  value={newChannelExclude}
                  onChange={(e) => setNewChannelExclude(e.target.value)}
                  placeholder="shorts&#10;Unboxing"
                  rows={2}
                />
              </div>
            </div>
            <div className="modal-actions">
              <button className="btn-cancel" onClick={() => setShowAddChannelModal(false)}>Cancel</button>
              <button
                className="btn-primary"
                onClick={handleAddChannel}
                disabled={actionLoading === 'add-channel'}
              >
                {actionLoading === 'add-channel' ? 'Adding...' : 'Add Channel'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Test Transcript Modal */}
      {showTestTranscriptModal && (
        <div className="modal-overlay" onClick={() => setShowTestTranscriptModal(false)}>
          <div className="modal modal-wide" onClick={(e) => e.stopPropagation()}>
            <h3>Test Transcript Fetching</h3>
            <div className="modal-content">
              <div className="form-group">
                <label>YouTube Video ID or URL:</label>
                <input
                  type="text"
                  value={testVideoId}
                  onChange={(e) => setTestVideoId(e.target.value)}
                  placeholder="dQw4w9WgXcQ or https://youtube.com/watch?v=..."
                />
                <small className="hint">Tests transcript fetching directly without Cloud Tasks queue</small>
              </div>

              {testResult && (
                <div className={`test-result ${testResult.success ? 'success' : 'error'}`}>
                  {testResult.success ? (
                    <>
                      <div className="result-header">
                        <span className="result-badge success">SUCCESS</span>
                        <span>Source: <strong>{testResult.source}</strong></span>
                        <span>Language: <strong>{testResult.language}</strong></span>
                        <span>Quality: <strong>{(testResult.quality_score * 100).toFixed(0)}%</strong></span>
                        <span>Length: <strong>{testResult.text_length}</strong> chars</span>
                      </div>
                      <div className="result-preview">
                        <label>Text Preview:</label>
                        <pre>{testResult.text_preview}</pre>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="result-header">
                        <span className="result-badge error">FAILED</span>
                        <span>Error: <strong>{testResult.error}</strong></span>
                      </div>
                      <div className="result-message">
                        {testResult.message}
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
            <div className="modal-actions">
              <button className="btn-cancel" onClick={() => setShowTestTranscriptModal(false)}>Close</button>
              <button
                className="btn-primary"
                onClick={handleTestTranscript}
                disabled={actionLoading === 'test-transcript'}
              >
                {actionLoading === 'test-transcript' ? 'Testing...' : 'Test Transcript'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminYouTube;
